import { useState } from "react";
import { Key, X, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useFirebaseAuth } from "@/hooks/use-firebase-auth";
import { requestPasswordReset } from "@/lib/firebase";
import { useToast } from "@/hooks/use-toast";

export function GooglePasswordSetupBanner() {
  const { user } = useFirebaseAuth();
  const { toast } = useToast();
  const [isSending, setIsSending] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  // Don't show if no user or banner was dismissed
  if (!user || isDismissed) return null;

  // Show for users who used Google auth and need to set up proper username/password
  const userProfile = user.profile;
  
  // Check if user is signed in via Google (has Google photo or signed in with Google)
  const hasGooglePhoto = user.photoURL && user.photoURL.includes('googleusercontent.com');
  const isGoogleUser = hasGooglePhoto || userProfile?.initialAuthMethod === 'google';
  
  // Check if username appears to be auto-set by Google (equals display name or full name)
  const displayName = user.displayName || '';
  const username = userProfile?.username || '';
  const name = userProfile?.name || '';
  
  // Check if user already has a password provider (meaning they've set up a password)
  const hasPasswordProvider = user.providerData?.some((provider: any) => provider.providerId === 'password');
  
  // Don't show banner if user already has a password set up
  if (!isGoogleUser || hasPasswordProvider) return null;
  
  // Show if username equals display name, full name, or looks auto-generated
  const usernameIsAutoGenerated = username === displayName || 
                                  username === name || 
                                  username.includes(' ') || // Usernames shouldn't have spaces
                                  !username; // No username set
  
  if (!usernameIsAutoGenerated) return null;

  const handleSetupPassword = async () => {
    setIsSending(true);
    try {
      if (!user.email) {
        throw new Error('No email address found');
      }
      await requestPasswordReset(user.email);
      toast({
        title: "Password Setup Email Sent",
        description: "Check your inbox for instructions to set up your password for email login. After setting up your password, refresh this page to update your account status.",
      });
      // Auto-dismiss after sending
      setIsDismissed(true);
    } catch (error: any) {
      let errorMessage = "Failed to send password setup email. Please try again.";
      if (error.code === 'auth/too-many-requests') {
        errorMessage = "Too many attempts. Please wait before trying again.";
      } else if (error.code === 'auth/user-token-expired') {
        errorMessage = "Session expired. Please log in again.";
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    // Store dismissal with timestamp - show again after 7 days
    const dismissalData = {
      timestamp: Date.now(),
      userId: user.id
    };
    localStorage.setItem("google-password-setup-dismissed", JSON.stringify(dismissalData));
  };

  // Check if previously dismissed (but allow after 7 days)
  const checkDismissal = () => {
    const dismissalData = localStorage.getItem("google-password-setup-dismissed");
    if (dismissalData) {
      try {
        const { timestamp, userId } = JSON.parse(dismissalData);
        // Show again if it's been 7 days OR different user
        const sevenDays = 7 * 24 * 60 * 60 * 1000;
        if (userId === user.id && Date.now() - timestamp < sevenDays) {
          return true; // Still dismissed
        }
      } catch (e) {
        // Invalid data, clear it
        localStorage.removeItem("google-password-setup-dismissed");
      }
    }
    return false; // Not dismissed or expired
  };

  if (checkDismissal()) {
    return null;
  }

  return (
    <div className="bg-gray-900 border-b border-gray-700">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 flex-1">
            <div className="bg-gray-800 rounded-full p-2">
              <Key className="h-5 w-5 text-brand" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-white">
                Complete your account setup
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                Set up a custom username and password for direct email login. Currently using: "{username}"
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                size="sm"
                onClick={handleSetupPassword}
                disabled={isSending}
                className="bg-brand text-black hover:bg-brand/90"
              >
                {isSending ? (
                  <>
                    <RefreshCw className="h-3 w-3 mr-1.5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Key className="h-3 w-3 mr-1.5" />
                    Complete Setup
                  </>
                )}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleDismiss}
                className="text-gray-400 hover:text-gray-300 hover:bg-gray-800"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}